const ACCESS_TOKEN_KEY = 'pc_access_token'
const REFRESH_TOKEN_KEY = 'pc_refresh_token'
const ADMIN_SESSION_KEY = 'pc_admin_session'

const isBrowser = () => typeof window !== 'undefined'

/**
 * Reading localStorage throws when a browser blocks site data, and that used to
 * stop every API call, sign-in included. This keeps them signed in until the
 * page is reloaded instead.
 */
const memory = new Map<string, string>()
const store = {
  getItem: (key: string): string | null => {
    try {
      return window.localStorage.getItem(key) ?? memory.get(key) ?? null
    } catch {
      return memory.get(key) ?? null
    }
  },
  removeItem: (key: string): void => {
    memory.delete(key)
    try {
      window.localStorage.removeItem(key)
    } catch {
      // nothing stored there to remove
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      window.localStorage.setItem(key, value)
      memory.delete(key)
    } catch {
      memory.set(key, value)
    }
  },
}

export const getAccessToken = (): string | null => (isBrowser() ? store.getItem(ACCESS_TOKEN_KEY) : null)

export const getRefreshToken = (): string | null => (isBrowser() ? store.getItem(REFRESH_TOKEN_KEY) : null)

export const setTokens = ({ accessToken, refreshToken }: { accessToken: string; refreshToken: string }): void => {
  if (!isBrowser()) return
  store.setItem(ACCESS_TOKEN_KEY, accessToken)
  store.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export const setAccessToken = (accessToken: string): void => {
  if (!isBrowser()) return
  store.setItem(ACCESS_TOKEN_KEY, accessToken)
}

export const clearTokens = (): void => {
  if (!isBrowser()) return
  store.removeItem(ACCESS_TOKEN_KEY)
  store.removeItem(REFRESH_TOKEN_KEY)
  store.removeItem(ADMIN_SESSION_KEY)
}

/**
 * Impersonation swaps in a short-lived access token for the target user and
 * deliberately drops the refresh token (stashing the admin's own tokens
 * instead) - if the impersonation token expires before the admin explicitly
 * stops, the axios refresh interceptor has nothing to refresh with and the
 * session dies cleanly, rather than silently minting a fresh token that
 * quietly reverts every request back to acting as the admin.
 */
export const isImpersonating = (): boolean => (isBrowser() ? store.getItem(ADMIN_SESSION_KEY) !== null : false)

export const startImpersonation = (impersonationAccessToken: string): void => {
  if (!isBrowser()) return
  const accessToken = getAccessToken()
  const refreshToken = getRefreshToken()
  if (accessToken && refreshToken) {
    store.setItem(ADMIN_SESSION_KEY, JSON.stringify({ accessToken, refreshToken }))
  }
  store.setItem(ACCESS_TOKEN_KEY, impersonationAccessToken)
  store.removeItem(REFRESH_TOKEN_KEY)
}

export const stopImpersonation = (): boolean => {
  if (!isBrowser()) return false
  const stashed = store.getItem(ADMIN_SESSION_KEY)
  if (!stashed) return false
  try {
    const { accessToken, refreshToken } = JSON.parse(stashed) as { accessToken: string; refreshToken: string }
    setTokens({ accessToken, refreshToken })
  } finally {
    store.removeItem(ADMIN_SESSION_KEY)
  }
  return true
}
